import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  AlertTriangle, CheckCircle, Clock, Filter, Search, MessageSquare,
  ChevronDown, ChevronUp, User, Calendar, FileText, Send, Eye,
  AlertCircle, XCircle, Scale, Trash2, Archive, Edit, Hash, X, Save
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { toast } from 'sonner';
import api from '../../services/api';

const DisputesTab = () => {
  const [disputes, setDisputes] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({ status: '', type: '', priority: '' });
  const [selectedDispute, setSelectedDispute] = useState(null);
  const [newNote, setNewNote] = useState('');
  const [isInternal, setIsInternal] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editData, setEditData] = useState({});
  const [statusChangeModal, setStatusChangeModal] = useState(false);
  const [newStatus, setNewStatus] = useState('');
  const [statusNotes, setStatusNotes] = useState('');

  const disputeTypes = [
    { value: 'customer_complaint', label: 'Customer Complaint' },
    { value: 'worker_dispute', label: 'Worker Dispute' },
    { value: 'payment_issue', label: 'Payment Issue' },
    { value: 'service_quality', label: 'Service Quality' },
    { value: 'time_dispute', label: 'Time Dispute' },
    { value: 'scheduling_issue', label: 'Scheduling Issue' },
    { value: 'safety_concern', label: 'Safety Concern' },
    { value: 'other', label: 'Other' }
  ];

  const statuses = [
    { value: 'open', label: 'Open', color: 'bg-[#D4AF37]/10 text-[#D4AF37]' },
    { value: 'under_review', label: 'Under Review', color: 'bg-[var(--warning)]/15 text-yellow-700' },
    { value: 'awaiting_response', label: 'Awaiting Response', color: 'bg-[#F4C2C2]/15 text-purple-700' },
    { value: 'resolved', label: 'Resolved', color: 'bg-[var(--success)]/15 text-green-700' },
    { value: 'closed', label: 'Closed', color: 'bg-[var(--background-subtle)] text-[var(--text-primary)]' },
    { value: 'escalated', label: 'Escalated', color: 'bg-[var(--error)]/15 text-red-700' },
    { value: 'archived', label: 'Archived', color: 'bg-slate-100 text-slate-700' }
  ];

  const priorities = [
    { value: 'low', label: 'Low', color: 'bg-[var(--background-subtle)] text-[var(--text-secondary)]' },
    { value: 'medium', label: 'Medium', color: 'bg-[#D4AF37]/10 text-[#D4AF37]' },
    { value: 'high', label: 'High', color: 'bg-orange-100 text-orange-600' },
    { value: 'urgent', label: 'Urgent', color: 'bg-[var(--error)]/15 text-[var(--error)]' }
  ];

  const resolutionTypes = [
    { value: 'refund_full', label: 'Full Refund' },
    { value: 'refund_partial', label: 'Partial Refund' },
    { value: 'service_credit', label: 'Service Credit' },
    { value: 'rebook_free', label: 'Free Rebook' },
    { value: 'worker_warning', label: 'Worker Warning' },
    { value: 'worker_suspension', label: 'Worker Suspension' },
    { value: 'no_action', label: 'No Action Required' },
    { value: 'customer_education', label: 'Customer Education' },
    { value: 'other', label: 'Other' }
  ];

  // State for creating new dispute
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createData, setCreateData] = useState({
    subject: '',
    description: '',
    dispute_type: 'customer_complaint',
    priority: 'medium',
    submitted_by_name: '',
    submitted_by_role: 'owner',
    related_booking_id: '',
    related_customer_name: '',
    related_worker_name: ''
  });

  useEffect(() => {
    fetchData();
     
  }, [filter]);

  const fetchData = async () => {
    try {
      const [disputesRes, statsRes] = await Promise.all([
        api.get(`/disputes?${new URLSearchParams(filter).toString()}`),
        api.get('/disputes/stats')
      ]);
      setDisputes(disputesRes.data);
      setStats(statsRes.data);
    } catch (error) {
      console.error('Failed to fetch disputes:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateDispute = async () => {
    if (!createData.subject || !createData.description) {
      toast.error('Please fill in subject and description');
      return;
    }
    if (createData.subject.length < 5) {
      toast.error('Subject must be at least 5 characters');
      return;
    }
    if (createData.description.length < 20) {
      toast.error('Description must be at least 20 characters');
      return;
    }
    setUpdating(true);
    try {
      // Format data to match backend expectations
      const payload = {
        dispute_type: createData.dispute_type,
        subject: createData.subject,
        description: createData.description,
        priority: createData.priority,
        // Names will be auto-filled from the logged-in user, but we can provide overrides
        related_customer_id: null,
        related_worker_id: null
      };
      
      await api.post('/disputes', payload);
      toast.success('Dispute created successfully');
      setShowCreateModal(false);
      setCreateData({
        subject: '',
        description: '',
        dispute_type: 'customer_complaint',
        priority: 'medium',
        submitted_by_name: '',
        submitted_by_role: 'owner',
        related_booking_id: '',
        related_customer_name: '',
        related_worker_name: ''
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create dispute');
    } finally {
      setUpdating(false);
    }
  };

  const handleUpdateStatus = async (disputeId, newStatus) => {
    setUpdating(true);
    try {
      await api.put(`/disputes/${disputeId}`, { status: newStatus });
      toast.success('Status updated');
      fetchData();
      if (selectedDispute?.id === disputeId) {
        const res = await api.get(`/disputes/${disputeId}`);
        setSelectedDispute(res.data);
      }
    } catch (error) {
      toast.error('Failed to update status');
    } finally {
      setUpdating(false);
    }
  };

  const handleResolve = async (disputeId, resolutionType, resolutionNotes) => {
    setUpdating(true);
    try {
      await api.put(`/disputes/${disputeId}`, { 
        status: 'resolved',
        resolution_type: resolutionType,
        resolution_notes: resolutionNotes
      });
      toast.success('Dispute resolved');
      fetchData();
      setSelectedDispute(null);
    } catch (error) {
      toast.error('Failed to resolve dispute');
    } finally {
      setUpdating(false);
    }
  };

  const handleDeleteDispute = async (disputeId) => {
    if (!window.confirm('Delete this dispute? This action cannot be undone.')) return;
    try {
      await api.delete(`/disputes/${disputeId}`);
      toast.success('Dispute deleted');
      fetchData();
      setSelectedDispute(null);
    } catch (error) {
      toast.error('Failed to delete dispute');
    }
  };

  const handleArchiveDispute = async (disputeId) => {
    try {
      await api.put(`/disputes/${disputeId}`, { status: 'archived' });
      toast.success('Dispute archived');
      fetchData();
      setSelectedDispute(null);
    } catch (error) {
      toast.error('Failed to archive dispute');
    }
  };

  const handleChangeStatus = async () => {
    if (!newStatus) {
      toast.error('Please select a status');
      return;
    }
    setUpdating(true);
    try {
      await api.put(`/disputes/${selectedDispute.id}`, { 
        status: newStatus,
        resolution_notes: statusNotes || undefined
      });
      toast.success(`Status changed to ${newStatus}`);
      setStatusChangeModal(false);
      setNewStatus('');
      setStatusNotes('');
      fetchData();
      // Refresh selected dispute
      const res = await api.get(`/disputes/${selectedDispute.id}`);
      setSelectedDispute(res.data);
    } catch (error) {
      toast.error('Failed to update status');
    } finally {
      setUpdating(false);
    }
  };

  const handleEditDispute = async () => {
    setUpdating(true);
    try {
      await api.put(`/disputes/${selectedDispute.id}`, editData);
      toast.success('Dispute updated');
      setEditMode(false);
      fetchData();
      // Refresh selected dispute
      const res = await api.get(`/disputes/${selectedDispute.id}`);
      setSelectedDispute(res.data);
    } catch (error) {
      toast.error('Failed to update dispute');
    } finally {
      setUpdating(false);
    }
  };

  const openEditMode = () => {
    setEditData({
      subject: selectedDispute.subject || '',
      description: selectedDispute.description || '',
      dispute_type: selectedDispute.dispute_type || '',
      priority: selectedDispute.priority || 'medium'
    });
    setEditMode(true);
  };

  const handleAddNote = async () => {
    if (!newNote.trim() || !selectedDispute) return;
    try {
      await api.post(`/disputes/${selectedDispute.id}/notes`, {
        note: newNote,
        is_internal: isInternal
      });
      toast.success('Note added');
      setNewNote('');
      const res = await api.get(`/disputes/${selectedDispute.id}`);
      setSelectedDispute(res.data);
    } catch (error) {
      toast.error('Failed to add note');
    }
  };

  const getStatusBadge = (status) => {
    const s = statuses.find(st => st.value === status);
    return s ? <span className={`px-2 py-1 rounded-full text-xs font-medium ${s.color}`}>{s.label}</span> : status;
  };

  const getPriorityBadge = (priority) => {
    const p = priorities.find(pr => pr.value === priority);
    return p ? <span className={`px-2 py-1 rounded-full text-xs font-medium ${p.color}`}>{p.label}</span> : priority;
  };

  if (loading) {
    return (
      <div className="p-12 text-center">
        <div className="animate-spin w-8 h-8 border-2 border-[var(--primary)] border-t-transparent rounded-full mx-auto mb-4"></div>
        <p className="text-[var(--text-muted)]">Loading disputes...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold font-['Cormorant_Garamond'] flex items-center gap-2">
            <Scale className="w-6 h-6 text-[var(--primary)]" />
            Dispute Resolution
          </h2>
          <p className="text-[var(--text-muted)] text-sm mt-1">Manage customer complaints and worker disputes</p>
        </div>
        <Button 
          onClick={() => setShowCreateModal(true)} 
          className="btn-primary"
          data-testid="create-dispute-btn"
        >
          <AlertTriangle className="w-4 h-4 mr-2" />
          New Dispute
        </Button>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <div className="card p-4 text-center">
            <p className="text-2xl font-bold text-[#D4AF37]">{stats.open}</p>
            <p className="text-sm text-[var(--text-muted)]">Open</p>
          </div>
          <div className="card p-4 text-center">
            <p className="text-2xl font-bold text-[var(--error)]">{stats.urgent_count}</p>
            <p className="text-sm text-[var(--text-muted)]">Urgent</p>
          </div>
          <div className="card p-4 text-center">
            <p className="text-2xl font-bold text-orange-600">{stats.escalated}</p>
            <p className="text-sm text-[var(--text-muted)]">Escalated</p>
          </div>
          <div className="card p-4 text-center">
            <p className="text-2xl font-bold text-[var(--success)]">{stats.resolved}</p>
            <p className="text-sm text-[var(--text-muted)]">Resolved</p>
          </div>
          <div className="card p-4 text-center">
            <p className="text-2xl font-bold">{stats.total}</p>
            <p className="text-sm text-[var(--text-muted)]">Total</p>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="card p-4">
        <div className="flex flex-wrap gap-4">
          <select
            value={filter.status}
            onChange={(e) => setFilter({ ...filter, status: e.target.value })}
            className="input-styled"
            data-testid="filter-status"
          >
            <option value="">All Statuses</option>
            {statuses.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
          <select
            value={filter.dispute_type}
            onChange={(e) => setFilter({ ...filter, dispute_type: e.target.value })}
            className="input-styled"
            data-testid="filter-type"
          >
            <option value="">All Types</option>
            {disputeTypes.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
          <select
            value={filter.priority}
            onChange={(e) => setFilter({ ...filter, priority: e.target.value })}
            className="input-styled"
            data-testid="filter-priority"
          >
            <option value="">All Priorities</option>
            {priorities.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
          </select>
        </div>
      </div>

      {/* Disputes List */}
      <div className="space-y-4">
        {disputes.length === 0 ? (
          <div className="card p-12 text-center">
            <CheckCircle className="w-16 h-16 text-[var(--success)] mx-auto mb-4" />
            <h3 className="text-lg font-bold mb-2">No Disputes</h3>
            <p className="text-[var(--text-muted)]">All clear! No disputes match your filters.</p>
          </div>
        ) : (
          disputes.map((dispute) => (
            <motion.div
              key={dispute.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="card p-5 hover:shadow-md transition-shadow"
              data-testid={`dispute-${dispute.id}`}
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 cursor-pointer" onClick={() => setSelectedDispute(dispute)}>
                  <div className="flex items-center gap-2 flex-wrap mb-2">
                    {/* Dispute ID Badge */}
                    <span className="flex items-center gap-1 text-xs bg-slate-100 text-slate-600 px-2 py-1 rounded-full font-mono">
                      <Hash className="w-3 h-3" />
                      {dispute.id?.slice(0, 8)}
                    </span>
                    {getStatusBadge(dispute.status)}
                    {getPriorityBadge(dispute.priority)}
                    <span className="text-xs bg-[var(--background-subtle)] text-[var(--text-secondary)] px-2 py-1 rounded-full">
                      {disputeTypes.find(t => t.value === dispute.dispute_type)?.label || dispute.dispute_type}
                    </span>
                  </div>
                  <h3 className="font-bold text-lg">{dispute.subject}</h3>
                  <p className="text-sm text-[var(--text-muted)] line-clamp-2 mt-1">{dispute.description}</p>
                  <div className="flex items-center gap-4 mt-3 text-xs text-[var(--text-muted)]">
                    <span className="flex items-center gap-1">
                      <User className="w-3 h-3" />
                      {dispute.submitted_by_name} ({dispute.submitted_by_role})
                    </span>
                    <span className="flex items-center gap-1">
                      <Calendar className="w-3 h-3" />
                      {new Date(dispute.created_at).toLocaleDateString()}
                    </span>
                    {dispute.notes?.length > 0 && (
                      <span className="flex items-center gap-1">
                        <MessageSquare className="w-3 h-3" />
                        {dispute.notes.length} notes
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" onClick={() => setSelectedDispute(dispute)} data-testid={`view-dispute-${dispute.id}`}>
                    <Eye className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-orange-500 hover:text-orange-700"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleArchiveDispute(dispute.id);
                    }}
                    title="Archive"
                    data-testid={`archive-dispute-${dispute.id}`}
                  >
                    <Archive className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-[var(--error)] hover:text-red-700 hover:bg-[var(--error)]/10"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteDispute(dispute.id);
                    }}
                    data-testid={`delete-dispute-${dispute.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </motion.div>
          ))
        )}
      </div>

      {/* Dispute Detail Modal */}
      <AnimatePresence>
        {selectedDispute && !editMode && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="card p-6 max-w-3xl w-full max-h-[90vh] overflow-y-auto my-4"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-start justify-between mb-6">
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    {/* Show ID */}
                    <span className="flex items-center gap-1 text-xs bg-slate-100 text-slate-600 px-2 py-1 rounded-full font-mono">
                      <Hash className="w-3 h-3" />
                      {selectedDispute.id?.slice(0, 8)}
                    </span>
                    {getStatusBadge(selectedDispute.status)}
                    {getPriorityBadge(selectedDispute.priority)}
                  </div>
                  <h2 className="text-xl font-bold">{selectedDispute.subject}</h2>
                </div>
                <div className="flex items-center gap-2">
                  <Button variant="ghost" size="sm" onClick={openEditMode} title="Edit">
                    <Edit className="w-4 h-4" />
                  </Button>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={() => { setStatusChangeModal(true); setNewStatus(selectedDispute.status); }}
                    title="Change Status"
                  >
                    <CheckCircle className="w-4 h-4" />
                  </Button>
                  <button onClick={() => setSelectedDispute(null)} className="p-2 hover:bg-[var(--background-subtle)] rounded-full">
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>

              {/* Dispute Details */}
              <div className="space-y-4 mb-6">
                <div className="bg-[var(--accent)] p-4 rounded-xl">
                  <p className="text-sm font-medium mb-1">Description</p>
                  <p className="text-[var(--text-secondary)]">{selectedDispute.description}</p>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm font-medium mb-1">Submitted By</p>
                    <p>{selectedDispute.submitted_by_name} ({selectedDispute.submitted_by_role})</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium mb-1">Type</p>
                    <p>{disputeTypes.find(t => t.value === selectedDispute.dispute_type)?.label}</p>
                  </div>
                  {selectedDispute.related_worker_name && (
                    <div>
                      <p className="text-sm font-medium mb-1">Related Worker</p>
                      <p>{selectedDispute.related_worker_name}</p>
                    </div>
                  )}
                  {selectedDispute.related_customer_name && (
                    <div>
                      <p className="text-sm font-medium mb-1">Related Customer</p>
                      <p>{selectedDispute.related_customer_name}</p>
                    </div>
                  )}
                </div>

                {selectedDispute.booking_details && (
                  <div className="border border-[var(--border-primary)] p-4 rounded-xl">
                    <p className="text-sm font-medium mb-2">Related Booking</p>
                    <p><strong>{selectedDispute.booking_details.service_name}</strong></p>
                    <p className="text-sm text-[var(--text-muted)]">
                      {selectedDispute.booking_details.scheduled_date} at {selectedDispute.booking_details.scheduled_time}
                    </p>
                    <p className="text-sm">${Number(selectedDispute.booking_details.total_amount || 0).toFixed(2)}</p>
                  </div>
                )}

                {selectedDispute.resolution_type && (
                  <div className="bg-[var(--success)]/10 border border-[var(--success)]/30 p-4 rounded-xl">
                    <p className="text-sm font-medium text-[var(--success)] mb-1">Resolution</p>
                    <p className="text-green-700">{resolutionTypes.find(r => r.value === selectedDispute.resolution_type)?.label}</p>
                    {selectedDispute.resolution_notes && (
                      <p className="text-sm text-[var(--success)] mt-2">{selectedDispute.resolution_notes}</p>
                    )}
                    <p className="text-xs text-[var(--success)] mt-2">
                      Resolved by {selectedDispute.resolved_by_name} on {new Date(selectedDispute.resolved_at).toLocaleString()}
                    </p>
                  </div>
                )}
              </div>

              {/* Actions */}
              {!['resolved', 'closed'].includes(selectedDispute.status) && (
                <div className="border-t border-[var(--border-primary)] pt-4 mb-6">
                  <p className="text-sm font-medium mb-3">Update Status</p>
                  <div className="flex flex-wrap gap-2">
                    {statuses.filter(s => s.value !== selectedDispute.status).map(s => (
                      <Button
                        key={s.value}
                        variant="outline"
                        size="sm"
                        onClick={() => handleUpdateStatus(selectedDispute.id, s.value)}
                        disabled={updating}
                      >
                        {s.label}
                      </Button>
                    ))}
                  </div>
                </div>
              )}

              {/* Notes */}
              <div className="border-t border-[var(--border-primary)] pt-4">
                <p className="text-sm font-medium mb-3">Notes ({selectedDispute.notes?.length || 0})</p>
                <div className="space-y-3 mb-4 max-h-48 overflow-y-auto">
                  {selectedDispute.notes?.map((note) => (
                    <div key={note.id} className={`p-3 rounded-lg ${note.is_internal ? 'bg-[var(--warning)]/10 border border-[var(--warning)]/30' : 'bg-[var(--background-subtle)]'}`}>
                      {note.is_internal && <span className="text-xs text-[var(--warning)] font-medium">Internal Note</span>}
                      <p className="text-sm">{note.note}</p>
                      <p className="text-xs text-[var(--text-muted)] mt-1">
                        {note.author_name} • {new Date(note.created_at).toLocaleString()}
                      </p>
                    </div>
                  ))}
                </div>
                <div className="flex gap-2">
                  <Textarea
                    value={newNote}
                    onChange={(e) => setNewNote(e.target.value)}
                    placeholder="Add a note..."
                    rows={2}
                    className="flex-1"
                  />
                </div>
                <div className="flex items-center justify-between mt-2">
                  <label className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={isInternal}
                      onChange={(e) => setIsInternal(e.target.checked)}
                      className="w-4 h-4 rounded"
                    />
                    Internal note (owner only)
                  </label>
                  <Button onClick={handleAddNote} disabled={!newNote.trim()} size="sm">
                    <Send className="w-4 h-4 mr-1" /> Add Note
                  </Button>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Edit Dispute Modal */}
      <AnimatePresence>
        {editMode && selectedDispute && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-[var(--background-paper)] rounded-xl shadow-xl w-full max-w-lg"
            >
              <div className="p-4 border-b flex items-center justify-between">
                <h2 className="text-lg font-bold">Edit Dispute</h2>
                <Button variant="ghost" size="sm" onClick={() => setEditMode(false)}>
                  <X className="w-4 h-4" />
                </Button>
              </div>
              <div className="p-4 space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Subject</label>
                  <Input
                    value={editData.subject || ''}
                    onChange={(e) => setEditData(prev => ({ ...prev, subject: e.target.value }))}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Description</label>
                  <Textarea
                    value={editData.description || ''}
                    onChange={(e) => setEditData(prev => ({ ...prev, description: e.target.value }))}
                    rows={4}
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Type</label>
                    <select
                      value={editData.dispute_type || ''}
                      onChange={(e) => setEditData(prev => ({ ...prev, dispute_type: e.target.value }))}
                      className="input-styled w-full"
                    >
                      {disputeTypes.map(t => (
                        <option key={t.value} value={t.value}>{t.label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Priority</label>
                    <select
                      value={editData.priority || 'medium'}
                      onChange={(e) => setEditData(prev => ({ ...prev, priority: e.target.value }))}
                      className="input-styled w-full"
                    >
                      {priorities.map(p => (
                        <option key={p.value} value={p.value}>{p.label}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
              <div className="p-4 border-t flex justify-end gap-2">
                <Button variant="outline" onClick={() => setEditMode(false)}>Cancel</Button>
                <Button onClick={handleEditDispute} disabled={updating} className="btn-primary">
                  <Save className="w-4 h-4 mr-1" /> Save Changes
                </Button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Change Status Modal */}
      <AnimatePresence>
        {statusChangeModal && selectedDispute && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-[var(--background-paper)] rounded-xl shadow-xl w-full max-w-md"
            >
              <div className="p-4 border-b flex items-center justify-between">
                <h2 className="text-lg font-bold">Change Dispute Status</h2>
                <Button variant="ghost" size="sm" onClick={() => setStatusChangeModal(false)}>
                  <X className="w-4 h-4" />
                </Button>
              </div>
              <div className="p-4 space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Select New Status</label>
                  <div className="grid grid-cols-2 gap-2">
                    {statuses.map(s => (
                      <button
                        key={s.value}
                        onClick={() => setNewStatus(s.value)}
                        className={`p-2 rounded-lg text-sm font-medium transition-all border-2 ${
                          newStatus === s.value 
                            ? 'border-[var(--primary)] bg-[var(--primary)]/10' 
                            : 'border-[var(--border-primary)] hover:border-[var(--border-primary)]'
                        } ${s.color}`}
                      >
                        {s.label}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Notes (Optional)</label>
                  <Textarea
                    value={statusNotes}
                    onChange={(e) => setStatusNotes(e.target.value)}
                    placeholder="Add notes about this status change..."
                    rows={2}
                  />
                </div>
              </div>
              <div className="p-4 border-t flex justify-end gap-2">
                <Button variant="outline" onClick={() => setStatusChangeModal(false)}>Cancel</Button>
                <Button onClick={handleChangeStatus} disabled={updating} className="btn-primary">
                  <CheckCircle className="w-4 h-4 mr-1" /> Update Status
                </Button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Create New Dispute Modal */}
      <AnimatePresence>
        {showCreateModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-[var(--background-paper)] rounded-xl shadow-xl w-full max-w-2xl my-4"
            >
              <div className="p-4 border-b flex items-center justify-between">
                <h2 className="text-lg font-bold flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-orange-500" />
                  Create New Dispute
                </h2>
                <Button variant="ghost" size="sm" onClick={() => setShowCreateModal(false)}>
                  <X className="w-4 h-4" />
                </Button>
              </div>
              <div className="p-4 space-y-4 max-h-[70vh] overflow-y-auto">
                <div>
                  <label className="block text-sm font-medium mb-1">Subject *</label>
                  <Input
                    value={createData.subject}
                    onChange={(e) => setCreateData(prev => ({ ...prev, subject: e.target.value }))}
                    placeholder="Brief summary of the dispute"
                    data-testid="dispute-subject"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Description *</label>
                  <Textarea
                    value={createData.description}
                    onChange={(e) => setCreateData(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="Detailed description of the issue..."
                    rows={4}
                    data-testid="dispute-description"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Type</label>
                    <select
                      value={createData.dispute_type}
                      onChange={(e) => setCreateData(prev => ({ ...prev, dispute_type: e.target.value }))}
                      className="input-styled w-full"
                      data-testid="dispute-type"
                    >
                      {disputeTypes.map(t => (
                        <option key={t.value} value={t.value}>{t.label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Priority</label>
                    <select
                      value={createData.priority}
                      onChange={(e) => setCreateData(prev => ({ ...prev, priority: e.target.value }))}
                      className="input-styled w-full"
                      data-testid="dispute-priority"
                    >
                      {priorities.map(p => (
                        <option key={p.value} value={p.value}>{p.label}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Submitted By (Name)</label>
                    <Input
                      value={createData.submitted_by_name}
                      onChange={(e) => setCreateData(prev => ({ ...prev, submitted_by_name: e.target.value }))}
                      placeholder="Enter name manually"
                      data-testid="dispute-submitter-name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Submitted By (Role)</label>
                    <select
                      value={createData.submitted_by_role}
                      onChange={(e) => setCreateData(prev => ({ ...prev, submitted_by_role: e.target.value }))}
                      className="input-styled w-full"
                    >
                      <option value="owner">Owner</option>
                      <option value="customer">Customer</option>
                      <option value="worker">Worker</option>
                    </select>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Related Customer Name (Optional)</label>
                  <Input
                    value={createData.related_customer_name}
                    onChange={(e) => setCreateData(prev => ({ ...prev, related_customer_name: e.target.value }))}
                    placeholder="Customer involved in dispute"
                    data-testid="dispute-customer-name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Related Worker Name (Optional)</label>
                  <Input
                    value={createData.related_worker_name}
                    onChange={(e) => setCreateData(prev => ({ ...prev, related_worker_name: e.target.value }))}
                    placeholder="Worker involved in dispute"
                    data-testid="dispute-worker-name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Related Booking ID (Optional)</label>
                  <Input
                    value={createData.related_booking_id}
                    onChange={(e) => setCreateData(prev => ({ ...prev, related_booking_id: e.target.value }))}
                    placeholder="Booking ID if applicable"
                  />
                </div>
              </div>
              <div className="p-4 border-t flex justify-end gap-2">
                <Button variant="outline" onClick={() => setShowCreateModal(false)}>Cancel</Button>
                <Button onClick={handleCreateDispute} disabled={updating} className="btn-primary" data-testid="submit-dispute-btn">
                  <AlertTriangle className="w-4 h-4 mr-1" /> Create Dispute
                </Button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default DisputesTab;
